import { useState, useEffect } from "react";
import { Search, Filter } from "lucide-react";
import axios from "axios";
import { StudyCard, StudyCardSkeleton } from "../components/StudyCard";
import { StudyDetailsModal } from "../components/StudyDetailsModal";
import { getStudyCountries } from "../lib/countries";
import { usePageMeta } from "../hooks/usePageMeta";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function CaregiverStudiesPage() {
  const [studies, setStudies] = useState([]);
  const [filteredStudies, setFilteredStudies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [countryFilter, setCountryFilter] = useState("");
  const [selectedStudy, setSelectedStudy] = useState(null);

  usePageMeta({
    path: "/caregiver/studies",
    title: "Caregiver studies open now , MediTalk",
    description:
      "Healthcare research studies currently looking for caregivers. Share what life caring for a loved one is really like and get paid for your time.",
  });

  useEffect(() => {
    fetchStudies();
  }, []);

  useEffect(() => {
    filterStudies();
  }, [searchTerm, countryFilter, studies]);

  const fetchStudies = async () => {
    try {
      const response = await axios.get(`${API}/studies?study_type=caregiver&active_only=true`);
      setStudies(response.data);
      setFilteredStudies(response.data);
    } catch (error) {
      console.error("Error fetching studies:", error);
    } finally {
      setLoading(false);
    }
  };

  const filterStudies = () => {
    let filtered = [...studies];

    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(
        (study) =>
          study.title.toLowerCase().includes(term) ||
          study.condition.toLowerCase().includes(term) ||
          study.description.toLowerCase().includes(term)
      );
    }

    if (countryFilter) {
      const needle = countryFilter.toLowerCase();
      filtered = filtered.filter((study) => {
        const list = getStudyCountries(study);
        if (list.length > 0) {
          return list.some((c) => c.name.toLowerCase() === needle);
        }
        return (study.country || "").toLowerCase().includes(needle);
      });
    }

    setFilteredStudies(filtered);
  };

  const handleRegister = (study) => {
    setSelectedStudy(study);
  };

  const countries = (() => {
    const seen = new Map();
    for (const s of studies) {
      for (const c of getStudyCountries(s)) {
        if (!seen.has(c.name)) seen.set(c.name, c);
      }
    }
    return [...seen.values()];
  })();

  return (
    <div className="min-h-screen">
      {/* Header - DARK */}
      <section className="bg-[var(--dark-bg)] py-16">
        <div className="container-main">
          <span className="badge badge-sage mb-4">Caregiver Studies</span>
          <h1 className="text-3xl md:text-4xl font-['Outfit'] font-bold text-white mb-4">
            Live Studies for Caregivers
          </h1>
          <p className="text-[var(--gray-light)] max-w-2xl">
            Browse current research opportunities for caregivers. Your perspective is invaluable.
          </p>
        </div>
      </section>

      {/* Filters - LIGHT */}
      <section className="py-8 bg-white border-b border-[var(--light-border)] sticky top-20 z-30">
        <div className="container-main">
          <div className="flex flex-col md:flex-row gap-4">
            {/* Search */}
            <div className="relative flex-1">
              <Search
                size={20}
                className="absolute left-4 top-1/2 -translate-y-1/2 text-[var(--text-muted)]"
              />
              <input
                type="text"
                placeholder="Search by condition or keyword..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full h-12 pl-12 pr-4 rounded-xl border border-[var(--light-border)] bg-[var(--light-card)] text-[var(--navy)] focus:border-[var(--lime)] focus:ring-2 focus:ring-[var(--lime)]/20 transition-all"
                data-testid="search-input"
              />
            </div>

            {/* Country Filter */}
            <div className="relative">
              <Filter
                size={20}
                className="absolute left-4 top-1/2 -translate-y-1/2 text-[var(--text-muted)]"
              />
              <select
                value={countryFilter}
                onChange={(e) => setCountryFilter(e.target.value)}
                className="h-12 pl-12 pr-8 rounded-xl border border-[var(--light-border)] bg-[var(--light-card)] text-[var(--navy)] focus:border-[var(--lime)] focus:ring-2 focus:ring-[var(--lime)]/20 transition-all appearance-none min-w-[180px]"
                data-testid="country-filter"
              >
                <option value="">All Countries</option>
                {countries.map((c) => (
                  <option key={c.name} value={c.name}>
                    {c.name}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>
      </section>

      {/* Studies Grid - LIGHT */}
      <section className="py-12 bg-[var(--light-card)]">
        <div className="container-main">
          {loading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              <StudyCardSkeleton variant="light" />
              <StudyCardSkeleton variant="light" />
              <StudyCardSkeleton variant="light" />
            </div>
          ) : filteredStudies.length > 0 ? (
            <>
              <p className="text-[var(--text-muted)] mb-6">
                Showing {filteredStudies.length} {filteredStudies.length === 1 ? "study" : "studies"}
              </p>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {filteredStudies.map((study) => (
                  <StudyCard key={study.id} study={study} onRegister={handleRegister} variant="light" viewerCountry={countryFilter || null} />
                ))}
              </div>
            </>
          ) : (
            <div className="text-center py-16">
              <p className="text-[var(--text-muted)] mb-4">No caregiver studies available at the moment.</p>
              <p className="text-[var(--text-muted)] text-sm">Check back soon or view patient studies that may be relevant.</p>
            </div>
          )}
        </div>
      </section>

      {selectedStudy && (
        <StudyDetailsModal
          study={selectedStudy}
          audience="caregiver"
          onClose={() => setSelectedStudy(null)}
        />
      )}
    </div>
  );
}
